import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ArrowLeft, MessageSquare, ChevronDown } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageAttachments } from "@/components/MessageAttachments";
import { MessageBody } from "@/components/MessageBody";
import { MessageComposer } from "@/components/MessageComposer";
import { useMessageAttachments } from "@/hooks/useMessageAttachments";
import { Heading, Text } from "@/components/Typography";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import { useActiveWorkspace } from "@/hooks/useActiveWorkspace";
import { programsApi } from "@/services/api";
import { tracking } from "@/utils/tracking";
import { formatDisplayDate } from "@/utils/formatDisplayDate";

export interface DirectMessageRecipient {
  userId: string;
  name: string;
}

interface DirectProgramMessageDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  programId: number;
  programName?: string;
  recipients: DirectMessageRecipient[];
}

const formatMessageTime = (dateStr: string) => formatDisplayDate(dateStr);

export const DirectProgramMessageDrawer = ({
  isOpen,
  onClose,
  onBack,
  programId,
  programName,
  recipients,
}: DirectProgramMessageDrawerProps) => {
  const { user } = useAuth();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState<string>(recipients[0]?.userId ?? "");
  const [messageInput, setMessageInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isAdvisor = useActiveWorkspace() === "advisor";
  const selfRoleLabel = isAdvisor ? "Expert" : "Achiever";
  const otherRoleLabel = isAdvisor ? "Achiever" : "Expert";

  const selectedRecipient = recipients.find((r) => r.userId === selectedUserId) ?? recipients[0];

  const queryKey = ["direct-program-messages", programId, selectedUserId];

  const { data: messages = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => programsApi.getDirectProgramMessagesByUser(programId, selectedUserId),
    enabled: isOpen && !!selectedUserId && !!programId,
  });

  // Staged files for the next message. Shared with the group composer so the
  // upload rules live in one place.
  const attachments = useMessageAttachments(programId);

  const { mutate: sendMessage, isPending: sending } = useMutation({
    mutationFn: ({ text, attachmentIds }: { text: string; attachmentIds: number[] }) =>
      programsApi.sendDirectProgramMessage(programId, selectedUserId, text, attachmentIds),
    onSuccess: (_data, { text }) => {
      tracking.programDirectMessageSent({
        program_id: String(programId),
        recipient_user_id: selectedUserId,
        message_length: text.length,
      });
      setMessageInput("");
      attachments.clear();
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      showErrorToast("Failed to send message", "Error");
    },
  });

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length]);

  // Sync selectedUserId when recipients change
  useEffect(() => {
    if (recipients.length > 0 && !recipients.find((r) => r.userId === selectedUserId)) {
      setSelectedUserId(recipients[0].userId);
    }
  }, [recipients, selectedUserId]);

  const handleSend = () => {
    const trimmed = messageInput.trim();
    // An attachment alone is enough to send.
    if ((!trimmed && attachments.pending.length === 0) || sending) return;
    sendMessage({ text: trimmed, attachmentIds: attachments.pending.map((a) => a.id) });
  };

  // Only mount in the DOM when open — portal ensures fixed positioning works regardless of scroll container
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className="absolute right-0 top-0 h-full w-full bg-theme-bg-surface shadow-xl flex flex-col overflow-hidden"
        style={{ maxWidth: "499px" }}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-theme-border flex-shrink-0">
            <div className="flex items-start gap-2">
              {/* Back button — pinned to left */}
              <button
                onClick={onBack ?? onClose}
                aria-label="Back"
                className="flex-shrink-0 mt-0.5 p-2 hover:bg-theme-bg-hover rounded-full transition-colors"
              >
                <ArrowLeft size={18} className="text-theme-text-muted" />
              </button>

              {/* Title + subtitle — grows to fill space, wraps on overflow */}
              <div className="flex-1 min-w-0 py-1">
                <Heading
                  as="h2"
                  className="font-inter font-bold text-20 leading-7 text-theme-text-heading break-words"
                >
                  {recipients.length === 1
                    ? selectedRecipient?.name ?? "Direct Message"
                    : "Direct Message"}
                </Heading>
                {programName && (
                  <Text as="p" className="font-inter text-13 text-theme-text-muted mt-0.5 break-words">
                    {programName}
                  </Text>
                )}
              </div>

              {/* Close button — pinned to right */}
              <button
                onClick={onClose}
                aria-label="Close"
                className="flex-shrink-0 mt-0.5 p-2 hover:bg-theme-bg-hover rounded-full transition-colors"
              >
                <X size={18} className="text-theme-text-muted" />
              </button>
            </div>

            {/* Recipient dropdown — shown when multiple advisors */}
            {recipients.length > 1 && (
              <div className="mt-3 relative">
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full appearance-none rounded-[10px] border border-theme-border bg-theme-bg-surface-raised px-4 py-2.5 font-inter text-14 text-theme-text-heading focus:outline-none focus:ring-2 focus:ring-primary-350 pr-9"
                >
                  {recipients.map((r) => (
                    <option key={r.userId} value={r.userId}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-theme-text-muted pointer-events-none"
                />
              </div>
            )}
          </div>

          {/* Messages list */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}>
                    <div className="max-w-[75%] rounded-2xl bg-theme-bg-surface-raised h-16 animate-pulse" />
                  </div>
                ))}
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-center">
                <MessageSquare size={28} className="text-theme-text-muted opacity-40" />
                <Text as="p" className="font-inter text-14 text-theme-text-muted">
                  No messages yet. Send a message below.
                </Text>
              </div>
            ) : (
              messages.map((msg) => {
                const isCurrentUser = msg.senderId === user?.id;
                const displayName = isCurrentUser ? "You" : msg.senderName;
                const roleLabel = isCurrentUser ? selfRoleLabel : otherRoleLabel;
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 pt-3 pb-3 ${
                        isCurrentUser
                          ? "bg-theme-msg-bubble-self-bg"
                          : "bg-theme-msg-bubble-other-bg"
                      }`}
                    >
                      <div className="mb-1 flex items-start justify-between gap-2">
                        <div className="flex min-w-0 flex-col">
                          <Text
                            as="span"
                            className={`font-inter text-14 font-semibold ${
                              isCurrentUser
                                ? "text-theme-msg-bubble-self-name"
                                : "text-theme-msg-bubble-other-name"
                            }`}
                          >
                            {displayName}
                          </Text>
                          <Text as="span" className="font-inter text-12 text-theme-text-muted">
                            {roleLabel}
                          </Text>
                        </div>
                        <Text as="span" className="font-inter text-12 text-theme-text-muted flex-shrink-0">
                          {formatMessageTime(msg.createdAt)}
                        </Text>
                      </div>
                      {msg.body && (
                        <MessageBody
                          text={msg.body}
                          className="font-inter text-14 text-theme-text leading-6"
                        />
                      )}
                      <MessageAttachments
                        attachments={msg.attachments ?? []}
                        programId={programId}
                      />
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Dashed separator */}
          <div className="border-t border-dashed border-theme-border" />

          <MessageComposer
            value={messageInput}
            onChange={setMessageInput}
            onSend={handleSend}
            sending={sending}
            autoFocus
            placeholder="Type a direct message..."
            attachments={attachments.pending}
            onAttachFiles={(files) => void attachments.attach(files)}
            onRemoveAttachment={attachments.remove}
            isUploading={attachments.isUploading}
            canAttach={attachments.canAttach}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DirectProgramMessageDrawer;
